import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ArrowBack } from '@material-ui/icons';
import CheckIcon from '@material-ui/icons/Check';
import DoneIcon from '@material-ui/icons/Done';
import { useJsApiLoader } from '@react-google-maps/api';
import clsx from 'clsx';
import { endOfDay } from 'date-fns';
import { FastField, Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import ReactDropzone from 'react-dropzone';
import LinesEllipsis from 'react-lines-ellipsis';
import * as Yup from 'yup';

import { Collapse } from '@material-ui/core';
import { useEditManualSupplierMutation, useOnboardManualSupplierMutation } from '../../../api/company';
import { DeliveryDay } from '../../../api/company/types';
import { useImportManualSupplierProductsMutation, usePreviewManualSupplierProductsMutation } from '../../../api/product';
import SvgDownload from '../../../assets/icons/Download';
import SvgFile from '../../../assets/icons/File';
import SvgUploadIcon from '../../../assets/icons/UploadIcon';
import Logo from '../../../assets/images/logo/Logo_White.svg';
import { AddressAutocomplete } from '../../../shared/components/address-autocomplete';
import { BusinessPlaceAutocomplete } from '../../../shared/components/business-place-autocomplete';
import { ButtonLoader } from '../../../shared/components/button-loader';
import { CommonButton } from '../../../shared/components/common-button';
import { FormikInput } from '../../../shared/components/formik-input';
import { ImageUploadManualS } from '../../../shared/components/image-upload-manual-s';
import { InfoTooltipComponent } from '../../../shared/components/info-tooltip-component';
import { PostalCodePlaceAutocomplete } from '../../../shared/components/postal-code-place-autocomplete';
import { ProgressBar } from '../../../shared/components/progress-bar';
import { CustomTimePicker } from '../../../shared/components/time-picker';
import { countries } from '../../../shared/constants/countries';
import { ProductsCategories } from '../../../shared/constants/products';
import { week } from '../../../shared/constants/week';
import { generateProductsSheetForManual } from '../../../shared/helpers/generateProductsSheetForManual';
import { getCategoryId } from '../../../shared/helpers/getCategoryId';
import { useScreenSize } from '../../../shared/hooks/use-screen-size';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { ToastService } from '../../../shared/services/toastService';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getIsImpersonated } from '../../../store/user';
import { backToAdminThunk } from '../../../store/user/user.actions';
import { CategoryDropdown } from '../SuppliersCategories/common/category-dropdown';
import { ImportManualProductPreview } from './import-preview';
import { PreviewProducts } from './preview-products';

import { useStyles } from './style';

export enum OnBoardManualSteps {
  BUSINESS = 'BUSINESS',
  CONTACTS = 'CONTACTS',
  DELIVERY = 'DELIVERY',
  DOWNLOAD_TEMPLATE = 'DOWNLOAD_TEMPLATE',
  PREVIEW = 'PREVIEW',
  IMPORT = 'IMPORT',
}

interface Values {
  business: string;
  address: string;
  address_1?: string;
  address_2?: string;
  suburb: string;
  state: string;
  postcode?: string;
  country?: string;
}

interface ContactValues {
  ordersEmail?: string;
  phone?: string;
  category?: string;
}

interface DeliveryValues {
  minOrder?: number;
  freeDeliveryAt?: number;
  deliveryFee?: number;
}

const initialValues: Values = {
  business: '',
  address: '',
  suburb: '',
  state: '',
};

const OnBoardManualSupplier: React.FC = () => {
  const classes = useStyles();
  const { isMobile, isDesktop } = useScreenSize();
  const { replace, goBack } = useHistory();
  const dispatch = useAppDispatch();
  const { state: navState } = useLocation<{ id: number } | null>();

  const [onBoardStep, setOnBoardStep] = useState<OnBoardManualSteps | null>(OnBoardManualSteps.BUSINESS);
  const [isAddressOpened, setIsAddressOpened] = useState(false);
  const [isFormOpened, setIsFormOpened] = useState(false);
  const [moreExpanded, setMoreExpanded] = useState(false);
  const [templateDownloaded, setTemplateDownloaded] = useState(false);
  const [formData, setFormData] = useState<Values>(initialValues);
  const [logo, setLogo] = useState<Blob | null>(null);
  const [supplierId, setSupplierId] = useState<{ name?: string; id?: number; verified?: boolean } | undefined>();
  const [contacts, setContacts] = useState<ContactValues>({});
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryValues>({});
  const [selectedDays, setSelectedDays] = useState<DeliveryDay[]>([
    DeliveryDay.MONDAY,
    DeliveryDay.TUESDAY,
    DeliveryDay.WEDNESDAY,
    DeliveryDay.THURSDAY,
    DeliveryDay.FRIDAY,
  ]);
  const [time, setTime] = useState<Date>(endOfDay(new Date()));
  const [fileName, setFileName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);

  const [onboard, { isLoading: onBoardLoading }] = useOnboardManualSupplierMutation();
  const [editManual, { isLoading: editLoading }] = useEditManualSupplierMutation();
  const impersonated = useAppSelector(getIsImpersonated);
  const [getData, { data, error, isLoading }] = usePreviewManualSupplierProductsMutation();
  const [importProducts, { isLoading: importLoading }] = useImportManualSupplierProductsMutation();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: ['places'],
  });

  const businessSchema = Yup.object().shape({
    business: Yup.string().required('Required field'),
    address: Yup.string().required('Required field'),
    suburb: Yup.string().required('Required field'),
  });

  const contactsSchema = Yup.object().shape({
    ordersEmail: Yup.string().email('Please enter a valid email'),
    category: Yup.string().required('Required field'),
  });

  const onBackToAdmin = () => {
    dispatch(backToAdminThunk());
  };

  const checkIsBusinessStepCompleted = (values: Values) => {
    return values.business && (values.address || values.postcode);
  };

  const backToSuppliersList = () => {
    replace('/account/suppliers');
  };

  const onChangeTime = (value: Date) => {
    setTime(value);
  };

  const calculateProgress = (step: OnBoardManualSteps | null) => {
    if (step === OnBoardManualSteps.BUSINESS) {
      return 10;
    }
    if (step === OnBoardManualSteps.CONTACTS) {
      return 30;
    }
    if (step === OnBoardManualSteps.DELIVERY) {
      return 50;
    }
    if (step === OnBoardManualSteps.DOWNLOAD_TEMPLATE) {
      return 60;
    }
    if (step === OnBoardManualSteps.PREVIEW) {
      return 85;
    }
    if (step === OnBoardManualSteps.IMPORT) {
      return 95;
    }
    return 0;
  };

  const setBusiness = (place: google.maps.places.PlaceResult) => {
    const business = place.name || '';
    const country = place.address_components?.find((el) => el.types.includes('country'))?.long_name || '';
    const state = place.address_components?.find((el) => el.types.includes('administrative_area_level_1'))?.long_name || '';
    const suburb = place.address_components?.find((el) => el.types.includes('locality'))?.long_name || '';
    const address = place.formatted_address || '';
    const postcode = place.address_components?.find((el) => el.types.includes('postal_code'))?.long_name || '';
    const street_number = place.address_components?.find((el) => el.types.includes('street_number'))?.long_name || '';
    const route = place.address_components?.find((el) => el.types.includes('route'))?.long_name || '';
    setFormData((prev) => ({
      ...prev,
      business,
      state,
      postcode,
      address,
      suburb,
      country,
      address_1: `${street_number} ${route}`.trim(),
    }));
  };

  const onSetDeliveryDay = (day: DeliveryDay) => {
    if (selectedDays.includes(day)) {
      setSelectedDays((state) => state.filter((item) => item !== day));
    } else {
      setSelectedDays((state) => [...state, day]);
    }
  };

  const setAddress = (place: google.maps.places.PlaceResult) => {
    const country = place.address_components?.find((el) => el.types.includes('country'))?.long_name || '';
    const state = place.address_components?.find((el) => el.types.includes('administrative_area_level_1'))?.long_name || '';
    const suburb = place.address_components?.find((el) => el.types.includes('locality'))?.long_name || '';
    const address = place.formatted_address || '';
    const postcode = place.address_components?.find((el) => el.types.includes('postal_code'))?.long_name || '';
    const street_number = place.address_components?.find((el) => el.types.includes('street_number'))?.long_name || '';
    const route = place.address_components?.find((el) => el.types.includes('route'))?.long_name || '';
    setFormData((prev) => ({
      ...prev,
      state,
      postcode,
      address,
      suburb,
      country,
      address_1: `${street_number} ${route}`.trim(),
    }));
  };

  const onSetPostCodeLoc = (place: google.maps.places.PlaceResult, setFieldValue: any, { business, address }: Values) => {
    const postcode = place.address_components?.find((el) => el.types.includes('postal_code'))?.long_name || '';
    if (!postcode) {
      return;
    }
    const country = place.address_components?.find((el) => el.types.includes('country'))?.long_name || '';
    const state = place.address_components?.find((el) => el.types.includes('administrative_area_level_1'))?.long_name || '';
    const suburb = place.address_components?.find((el) => el.types.includes('locality'))?.long_name || '';
    setFormData((prev) => ({ ...prev, postcode, state, country, suburb, business, address }));
  };

  const openAddressForm = () => {
    setIsFormOpened(true);
    setFormData((prev) => ({
      ...initialValues,
      business: prev.business,
      address: prev.address,
    }));
  };

  const openAddressField = () => {
    setIsAddressOpened(true);
    setFormData((prev) => ({
      ...initialValues,
      business: prev.business,
    }));
  };

  const onUpload = () => {
    if (uploadedFile && supplierId?.id) {
      const xlsxData = new FormData();
      xlsxData.append('file', uploadedFile);
      xlsxData.append('supplier_id', supplierId.id.toString());
      setOnBoardStep(OnBoardManualSteps.PREVIEW);
      getData(xlsxData);
    }
  };

  const onImport = () => {
    if (uploadedFile && supplierId?.id) {
      const xlsxData = new FormData();
      xlsxData.append('file', uploadedFile);
      xlsxData.append('supplier_id', supplierId.id.toString());
      importProducts(xlsxData).then((res) => {
        if ('data' in res && res?.data?.success) {
          onOnboardSuccess();
        }
      });
    }
  };

  const onOnboardSuccess = () => {
    ToastService.success(`${formData.business || 'Supplier'} has been added and is ready for you to place your first order`);
    replace('/account/suppliers', supplierId);
  };

  const onSubmit = (values: Values, formikHelpers: FormikHelpers<Values>) => {
    formikHelpers.setSubmitting(false);
    setFormData((prev) => ({ ...prev, ...values, state: prev.state }));
    setOnBoardStep(OnBoardManualSteps.CONTACTS);
  };

  const finishOnBoarding = async (values: DeliveryValues) => {
    if (checkIsBusinessStepCompleted(formData)) {
      const reqBody = new FormData();
      reqBody.append('company[name]', formData.business);
      logo && reqBody.append('company[picture]', logo);
      reqBody.append('company[addresses_attributes][0][city]', formData.suburb);
      reqBody.append(
        'company[addresses_attributes][0][country]',
        formData.country || countries.find((el) => el.value === LocalStorageService.getItem('country'))?.label || '',
      );
      reqBody.append('company[addresses_attributes][0][full_address]', formData.address || ' ');
      reqBody.append('company[addresses_attributes][0][postcode]', formData.postcode || ' ');
      reqBody.append('company[addresses_attributes][0][state]', formData.state || ' ');
      formData.address_1 && reqBody.append('company[addresses_attributes][0][street_address1]', formData.address_1);

      values.minOrder !== undefined &&
        reqBody.append('company[delivery_details_attributes][0][minimum_order_value_cents]', (values.minOrder * 100).toString());
      values.deliveryFee !== undefined &&
        reqBody.append('company[delivery_details_attributes][0][delivery_fee_cents]', (values.deliveryFee * 100).toString());
      time && reqBody.append('company[delivery_details_attributes][0][cutoff]', new Date(time).toISOString());
      values.freeDeliveryAt !== undefined &&
        reqBody.append('company[delivery_details_attributes][0][amount_for_free_delivery_cents]', (values.freeDeliveryAt * 100).toString());

      reqBody.append(`company[contacts_attributes][0][contact_type]`, 'order_contact');
      reqBody.append(`company[contacts_attributes][0][email]`, contacts.ordersEmail || '');
      reqBody.append('company[contacts_attributes][0][is_order_email]', 'true');
      reqBody.append(`company[contacts_attributes][1][contact_type]`, 'office');
      reqBody.append(`company[contacts_attributes][1][phone_number]`, contacts.phone || '');
      reqBody.append(`company[suppliers_attributes][0][email]`, contacts.ordersEmail || '');
      reqBody.append(
        `company[company_categories_attributes][0][category_id]`,
        getCategoryId(contacts.category as ProductsCategories).toString(),
      );

      week.forEach((day) => {
        reqBody.append(`company[delivery_details_attributes][0][${day.key}]`, selectedDays.includes(day.key).toString());
      });
      const res = await onboard(reqBody);
      if ('data' in res && res.data?.success) {
        const supplierInfoToSave = {
          id: res.data?.company?.suppliers[0]?.id,
          name: res.data?.company?.name,
          verified: res?.data?.company?.suppliers[0]?.verified,
        };
        setSupplierId(supplierInfoToSave);
        LocalStorageService.setItem('manual_supplier_id', supplierInfoToSave);
        setOnBoardStep(OnBoardManualSteps.DOWNLOAD_TEMPLATE);
      }
    }
  };

  const finishEditManual = async (values: DeliveryValues) => {
    const reqBody = new FormData();
    reqBody.append('setup_manual_supplier', 'true');
    navState?.id && reqBody.append('supplier_id', navState.id.toString());
    logo && reqBody.append('company[picture]', logo);
    values.minOrder !== undefined &&
      reqBody.append('company[delivery_details_attributes][0][minimum_order_value_cents]', (values.minOrder * 100).toString());
    values.deliveryFee !== undefined &&
      reqBody.append('company[delivery_details_attributes][0][delivery_fee_cents]', (values.deliveryFee * 100).toString());
    time && reqBody.append('company[delivery_details_attributes][0][cutoff]', new Date(time).toISOString());
    values.freeDeliveryAt !== undefined &&
      reqBody.append('company[delivery_details_attributes][0][amount_for_free_delivery_cents]', (values.freeDeliveryAt * 100).toString());

    week.forEach((day) => {
      reqBody.append(`company[delivery_details_attributes][0][${day.key}]`, selectedDays.includes(day.key).toString());
    });
    const res = await editManual(reqBody);
    if ('data' in res && res.data?.success) {
      const supplierInfoToSave = {
        id: res.data?.company?.suppliers[0]?.id,
        name: res.data?.company?.name,
        verified: res?.data?.company?.suppliers[0]?.verified,
      };
      setSupplierId(supplierInfoToSave);
      LocalStorageService.setItem('manual_supplier_id', supplierInfoToSave);
      setOnBoardStep(OnBoardManualSteps.DOWNLOAD_TEMPLATE);
    }
  };

  const onSubmitContacts = (values: ContactValues, formikHelpers: FormikHelpers<ContactValues>) => {
    formikHelpers.setSubmitting(false);
    setContacts(values);
    setOnBoardStep(OnBoardManualSteps.DELIVERY);
  };

  const onSubmitDeliveryDetails = (values: DeliveryValues, formikHelpers: FormikHelpers<DeliveryValues>) => {
    formikHelpers.setSubmitting(false);
    setDeliveryDetails(values);
    navState?.id ? finishEditManual(values) : finishOnBoarding(values);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadedFile(acceptedFiles[0]);
    setFileName(acceptedFiles[0].name);
  }, []);

  const onDownloadTemplate = async () => {
    await generateProductsSheetForManual();
    setTemplateDownloaded(true);
  };

  useEffect(() => {
    setIsAddressOpened(false);
    setIsFormOpened(false);
  }, [onBoardStep]);

  useEffect(() => {
    if (onBoardStep !== OnBoardManualSteps.PREVIEW || error) {
      setUploadPercent(0);
      return;
    }
    const uploadTimer = setInterval(() => {
      setUploadPercent((state) => {
        if (state < 95) {
          return state + 5;
        } else {
          clearInterval(uploadTimer);
          return state;
        }
      });
      if (!isLoading) {
        setUploadPercent(100);
        clearInterval(uploadTimer);
      }
    }, 700);
  }, [onBoardStep, isLoading]);

  useEffect(() => {
    const savedSupplier = LocalStorageService.getItem('manual_supplier_id');
    setSupplierId(savedSupplier || undefined);
    savedSupplier && setOnBoardStep(OnBoardManualSteps.DOWNLOAD_TEMPLATE);
    navState?.id && setOnBoardStep(OnBoardManualSteps.DELIVERY);
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <div className={classes.logoBox}>
          <img src={Logo} alt='OpenPantry' className={classes.logo} />
        </div>
        <div className={classes.progressWrap}>
          <ProgressBar percent={calculateProgress(onBoardStep)} />
        </div>

        {onBoardStep === OnBoardManualSteps.BUSINESS && (
          <>
            {isFormOpened ? (
              <Formik initialValues={formData} onSubmit={onSubmit} validationSchema={businessSchema} enableReinitialize={true}>
                {({ submitForm, isValid, setFieldError, setFieldValue, values }) => (
                  <Form className={classes.formBox}>
                    <div className={classes.title}>
                      Enter Supplier’s <br /> business address
                    </div>
                    <div className={classes.fieldWrap}>
                      <Field name='business'>
                        {(fieldProps: FieldProps) => <FormikInput {...fieldProps} label='Business Name' placeholder='Business Name' />}
                      </Field>
                    </div>
                    <div className={classes.fieldWrap}>
                      <Field name='address'>
                        {(fieldProps: FieldProps) => (
                          <FormikInput {...fieldProps} label='Address Line 1' placeholder='Address Line 1' autoComplete='chrome-off' />
                        )}
                      </Field>
                    </div>
                    <div className={classes.fieldWrap}>
                      <Field name='address_1'>
                        {(fieldProps: FieldProps) => (
                          <FormikInput {...fieldProps} label='Address Line 2' placeholder='Address Line 2' autoComplete='chrome-off' />
                        )}
                      </Field>
                    </div>
                    <div className={classes.smallFieldsBox}>
                      <div className={classes.smallFieldWrap}>
                        <PostalCodePlaceAutocomplete
                          setFieldError={setFieldError}
                          placeholder='Postcodе'
                          setPlace={(place) => {
                            onSetPostCodeLoc(place, setFieldValue, values);
                          }}
                          label='Postcodе'
                        />
                      </div>
                      <div className={classes.smallFieldWrap}>
                        <FastField name='suburb'>
                          {(fieldProps: FieldProps) => <FormikInput placeholder='Suburb' label='Suburb' {...fieldProps} />}
                        </FastField>
                      </div>
                      <div className={classes.smallFieldWrap}>
                        <FastField name='state'>
                          {(fieldProps: FieldProps) => <FormikInput placeholder='State/City' label='State/City' {...fieldProps} />}
                        </FastField>
                      </div>
                    </div>
                    <div className={classes.btnBox}>
                      <CommonButton onClick={backToSuppliersList} title='Back' width={215} buttonStyle='secondary' />
                      <CommonButton disabled={!isValid || !formData.postcode} onClick={submitForm} title='Next' width={215} />
                    </div>
                    <input type='text' name='postcode' placeholder='postcode' className='hiddenInput' />
                  </Form>
                )}
              </Formik>
            ) : (
              <>
                <div className={classes.title}>
                  {isAddressOpened ? (
                    <span>
                      Enter Supplier’s business
                      <br />
                      name & address
                    </span>
                  ) : (
                    <span>
                      Tell us a little more <br />
                      about this Supplier
                    </span>
                  )}
                </div>
                {isLoaded && (
                  <div className={classes.fieldWrap}>
                    <BusinessPlaceAutocomplete placeholder='Search for business...' setPlace={setBusiness} label='Business Name' />
                  </div>
                )}
                {isAddressOpened && isLoaded && (
                  <div className={classes.fieldWrap}>
                    <AddressAutocomplete placeholder='Start typing address...' setPlace={setAddress} label='Address' />
                  </div>
                )}
                <div className={classes.notFoundText} onClick={isAddressOpened ? openAddressForm : openAddressField}>
                  {isAddressOpened ? 'I can’t find the address' : 'I can’t find the business'}
                </div>
              </>
            )}
            {!isFormOpened && (
              <div className={classes.btnBox}>
                <CommonButton onClick={backToSuppliersList} title='Back' width={215} buttonStyle='secondary' />
                <CommonButton
                  onClick={() => {
                    setOnBoardStep(OnBoardManualSteps.CONTACTS);
                  }}
                  title='Next'
                  width={215}
                  disabled={!checkIsBusinessStepCompleted(formData)}
                />
              </div>
            )}
          </>
        )}

        {onBoardStep === OnBoardManualSteps.CONTACTS && (
          <Formik initialValues={contacts} onSubmit={onSubmitContacts} validationSchema={contactsSchema} enableReinitialize={true}>
            {({ submitForm, isValid, values }) => (
              <Form className={classes.formBox}>
                <div className={classes.title}>
                  Enter the contact details <br /> of this Supplier
                </div>
                <div className={classes.fieldWrap}>
                  <Field name='ordersEmail'>
                    {(fieldProps: FieldProps) => (
                      <FormikInput {...fieldProps} label='Email Address for Orders *' placeholder='Email address' autoComplete='on' />
                    )}
                  </Field>
                </div>
                <div className={classes.fieldWrap}>
                  <Field name='phone'>
                    {(fieldProps: FieldProps) => <FormikInput {...fieldProps} label='Contact Phone Number' placeholder='Phone Number' />}
                  </Field>
                </div>
                <div className={classes.fieldWrap}>
                  <CategoryDropdown />
                </div>
                <div className={classes.btnBox}>
                  <CommonButton
                    onClick={() => {
                      setOnBoardStep(OnBoardManualSteps.BUSINESS);
                    }}
                    title='Back'
                    width={215}
                    buttonStyle='secondary'
                  />
                  <CommonButton onClick={submitForm} title='Next' width={215} disabled={!isValid || !values.ordersEmail} />
                </div>
                <input type='text' name='postcode' placeholder='postcode' className='hiddenInput' />
              </Form>
            )}
          </Formik>
        )}

        {onBoardStep === OnBoardManualSteps.DELIVERY && (
          <Formik initialValues={deliveryDetails} onSubmit={onSubmitDeliveryDetails} enableReinitialize={true}>
            {({ submitForm, values }) => (
              <Form className={classes.formBox}>
                <>
                  <div className={classes.title}> Just a little more info</div>
                  <div className={classes.subTitle}>If you aren’t sure, you can click next</div>

                  <div className={classes.detailsBox}>
                    <ImageUploadManualS saveImage={setLogo} clearLogo={setLogo.bind(null, null)} />
                    <div className={classes.detailsTitle}>Delivery Days</div>
                    <div className={classes.detailsSupTitle}>Select Delivery Days of your Supplier</div>
                    <div className={classes.weekDaysBox}>
                      {week.map(({ title, key }) => (
                        <div
                          key={key}
                          className={clsx({
                            [classes.weekDayItem]: true,
                            [classes.weekDayItemSelected]: selectedDays.includes(key),
                          })}
                          onClick={onSetDeliveryDay.bind(null, key)}
                        >
                          <CheckIcon className={classes.checkIcon} />
                          {title}
                        </div>
                      ))}
                    </div>
                    <div className={classes.detailsTitle}>Order Settings</div>
                    <div className={classes.detailFieldWrap}>
                      <div className={classes.cutOffLabel}>Order cut off time</div>
                      <CustomTimePicker time={time || endOfDay(new Date())} onSetTime={onChangeTime} />
                    </div>
                    <InfoTooltipComponent text={'Optional Fields'}>
                      <div
                        onClick={() => setMoreExpanded((prev) => !prev)}
                        className={clsx(classes.detailsTitle, classes.deliverySettingsT, moreExpanded && classes.deliverySettingsTExpanded)}
                      >
                        More Delivery Settings
                        <svg width='6' height='9' viewBox='0 0 6 9' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <path
                            fill-rule='evenodd'
                            clip-rule='evenodd'
                            d='M0.248959 0.502866C0.580905 0.17092 1.1191 0.17092 1.45104 0.502866L4.95104 4.00287C5.28299 4.33481 5.28299 4.873 4.95104 5.20495L1.45104 8.70495C1.11909 9.03689 0.580905 9.03689 0.248959 8.70495C-0.0829868 8.373 -0.0829867 7.83481 0.248959 7.50287L3.14792 4.60391L0.248959 1.70495C-0.0829865 1.373 -0.0829864 0.834811 0.248959 0.502866Z'
                            fill='#152144'
                          />
                        </svg>
                      </div>
                    </InfoTooltipComponent>
                    <Collapse in={moreExpanded}>
                      <div className={classes.detailsRow}>
                        <div>
                          <Field name='minOrder'>
                            {(fieldProps: FieldProps) => (
                              <FormikInput
                                {...fieldProps}
                                label={isMobile ? 'Min. order value' : 'Minimum order value'}
                                placeholder='20$'
                                type='number'
                              />
                            )}
                          </Field>
                        </div>
                        <div>
                          <Field name='deliveryFee'>
                            {(fieldProps: FieldProps) => (
                              <FormikInput {...fieldProps} label='Delivery Fee' placeholder='20$' type='number' />
                            )}
                          </Field>
                        </div>
                        <div>
                          <Field name='freeDeliveryAt'>
                            {(fieldProps: FieldProps) => (
                              <FormikInput {...fieldProps} label='Free Delivery at' placeholder='20$' type='number' />
                            )}
                          </Field>
                        </div>
                      </div>
                    </Collapse>
                  </div>

                  <div className={classes.btnBox}>
                    <CommonButton
                      onClick={navState?.id ? goBack : setOnBoardStep.bind(null, OnBoardManualSteps.CONTACTS)}
                      title='Back'
                      width={215}
                      buttonStyle='secondary'
                    />
                    <CommonButton onClick={submitForm} title='Next' width={215} disabled={onBoardLoading || editLoading} />
                  </div>
                  <div
                    className={clsx([classes.notFoundText, classes.addLater])}
                    onClick={
                      onBoardLoading || editLoading
                        ? () => {}
                        : navState?.id
                        ? finishEditManual.bind(null, values)
                        : finishOnBoarding.bind(null, values)
                    }
                  >
                    I’ll add this later
                  </div>
                </>
              </Form>
            )}
          </Formik>
        )}

        {onBoardStep === OnBoardManualSteps.DOWNLOAD_TEMPLATE && (
          <>
            <div className={classes.title}>
              Upload your prices with this <br /> Supplier
            </div>
            <div className={clsx(classes.subTitle, classes.uploadSubTitle)}>
              Please upload your prices with this supplier. <br />
              You can download a template and then copy and paste your prices into the spreadsheet <br /> and upload it when you are ready
              to go.
            </div>
            {templateDownloaded ? (
              <div className={classes.uploadContainerWrap}>
                <ReactDropzone onDrop={onDrop} accept='.xlsx'>
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div className={clsx(classes.uploadContainer, isDragActive && classes.focused)} {...getRootProps()}>
                      <SvgUploadIcon className={classes.uploadIcon} />
                      <input {...getInputProps()} />
                      <div className={classes.uploadDescription}>
                        {!fileName ? (
                          isDesktop ? (
                            <span>
                              Drop file here to upload or <b className={classes.link}>choose a file</b>
                            </span>
                          ) : (
                            <span>Choose file to upload</span>
                          )
                        ) : (
                          <span>{fileName}</span>
                        )}
                      </div>
                    </div>
                  )}
                </ReactDropzone>
              </div>
            ) : (
              <>
                <SvgDownload className={classes.downloadIcon} onClick={onDownloadTemplate} />
                <CommonButton
                  onClick={onDownloadTemplate}
                  title='Download the template'
                  buttonStyle='white'
                  className={classes.downTemplateBtn}
                />
              </>
            )}

            <div className={classes.btnBox}>
              <CommonButton
                onClick={() => {
                  setOnBoardStep(OnBoardManualSteps.CONTACTS);
                }}
                title='Back'
                width={215}
                buttonStyle='secondary'
                disabled={!!supplierId}
              />
              <CommonButton onClick={onUpload} title='Upload' width={215} disabled={!uploadedFile} />
            </div>
            <div className={clsx([classes.notFoundText, classes.addLater])} onClick={onOnboardSuccess}>
              Skip - I will do this later
            </div>
          </>
        )}

        {onBoardStep === OnBoardManualSteps.PREVIEW && (
          <>
            <div className={classes.title}>Upload the completed template</div>
            <div className={clsx(classes.subTitle, classes.uploadSubTitle)}>
              Once you’ve added all of your data, save your spreadsheet and upload as
            </div>
            <div className={classes.formats}>
              <div>.XLSX</div>
            </div>
            <>
              <div className={classes.uploadProgressContainer}>
                <SvgFile className={classes.fileIcon} />
                <div>
                  <div className={classes.topBox}>
                    <div className={classes.boldText}>{fileName}</div>
                    {error || data?.added?.length === 0 ? (
                      <div className={classes.error}>{data?.added?.length === 0 ? 'List is empty' : 'Errors in file data'}</div>
                    ) : uploadPercent < 100 || isLoading ? (
                      <div>{uploadPercent === 100 ? 99 : uploadPercent}%</div>
                    ) : (
                      <div>
                        {isMobile ? `${uploadPercent}%` : 'Products Loaded Successfully'} <DoneIcon className={classes.doneIcon} />
                      </div>
                    )}
                  </div>
                  <ProgressBar percent={uploadPercent} />
                </div>
              </div>
              {uploadPercent === 100 && data && (
                <>
                  <div className={classes.errorsBlockWrap}>
                    <div className={classes.errorsBlock}>
                      {data?.error_data.length ? (
                        <LinesEllipsis
                          text={`${data?.error_data.length} File errors: ${data.error_data
                            .slice(0, 10)
                            .map((el) =>
                              'field_errors' in el
                                ? Object.entries(Object.values(el.field_errors)[0]).reduce((acc, [field, err]) => {
                                    acc += `${field} - ${err}, `;
                                    return acc;
                                  }, '')
                                : '',
                            )
                            .join(' ')}`}
                          maxLine={2}
                          ellipsis='...'
                        />
                      ) : null}
                    </div>
                  </div>
                  <PreviewProducts data={data} />
                </>
              )}
            </>
            <div className={classes.btnBox}>
              <CommonButton
                onClick={() => {
                  setOnBoardStep(OnBoardManualSteps.DOWNLOAD_TEMPLATE);
                  setUploadedFile(null);
                  setFileName('');
                }}
                title='Back'
                width={215}
                buttonStyle='secondary'
                disabled={isLoading}
              />
              <CommonButton
                onClick={() => {
                  setOnBoardStep(OnBoardManualSteps.IMPORT);
                }}
                title='Next'
                width={215}
                disabled={isLoading || !data?.added?.length}
              />
            </div>
          </>
        )}

        {onBoardStep === OnBoardManualSteps.IMPORT && (
          <>
            <div className={classes.title}>Preview your data</div>
            <div className={classes.subTitle}>
              Double check to make sure your data is being uploaded into the correct Open Pantry fields. <br /> Once imported this step
              cannot be undone
            </div>
            <ImportManualProductPreview data={data?.added ? data.added.slice(0, isMobile ? 1 : 4) : []} />
            <div className={classes.btnBox}>
              <CommonButton
                onClick={() => {
                  setOnBoardStep(OnBoardManualSteps.DOWNLOAD_TEMPLATE);
                  setFileName('');
                  setUploadedFile(null);
                }}
                title='Try uploading again'
                width={215}
                buttonStyle='secondary'
                disabled={importLoading}
              />
              <CommonButton
                onClick={onImport}
                title={importLoading ? 'Loading...' : 'I’m happy with this'}
                width={215}
                disabled={importLoading}
                endIcon={importLoading ? <ButtonLoader /> : null}
              />
            </div>
          </>
        )}
      </div>
      {impersonated && (
        <CommonButton
          onClick={onBackToAdmin}
          title='Return to admin'
          buttonStyle='blueButton'
          isSmall={true}
          startIcon={<ArrowBack />}
          className={classes.backToAdmin}
          width={180}
        />
      )}
    </div>
  );
};

export default OnBoardManualSupplier;
